import React, { useState, useEffect } from 'react';
import { Menu, Search, ShoppingBag, User, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { NAVIGATION_ITEMS } from '../../constants';
import { NavbarProps } from '../../types';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';

const DEFAULT_LOGO = '/assets/logo/4HVNSK8-logo-black.svg';

const AppHeader: React.FC<NavbarProps> = ({ currentView }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);

  const { cartCount, openCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);

    // CMS-managed logo (content/branding); falls back to the built-in mark
    const fetchBranding = async () => {
      try {
        const snap = await getDoc(doc(db, 'content', 'branding'));
        if (snap.exists() && snap.data().logoUrl) {
          setLogoUrl(snap.data().logoUrl);
        }
      } catch {
        // Keep the default logo silently
      }
    };
    fetchBranding();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Determine if we need inversion (white text on dark background)
  const isTransparent = currentView === 'home' && !isScrolled && !isMobileMenuOpen;

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          isTransparent ? 'bg-transparent text-white' : 'bg-white text-black shadow-sm'
        }`}
      >
        <div className="container mx-auto px-4 md:px-8 h-20 flex items-center justify-between">

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Logo */}
          <div className="flex-1 lg:flex-none flex justify-center lg:justify-start">
            <Link
              to="/"
              className="block"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <img
                src={logoUrl}
                alt="4HVNSK8"
                onError={(e) => {
                  const t = e.currentTarget;
                  if (t.src !== window.location.origin + DEFAULT_LOGO) { t.src = DEFAULT_LOGO; }
                }}
                className={`h-14 w-auto transition-all duration-300 ${
                 isTransparent ? 'brightness-0 invert' : ''
                }`}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex flex-1 justify-center space-x-8">
            {NAVIGATION_ITEMS.map((item) => (
              <Link
                key={item.label}
                to={item.href}
                className={`font-heading font-medium text-sm tracking-widest hover:opacity-100 opacity-90 transition-opacity flex items-center h-20 border-b-2 border-transparent ${
                  currentView === item.view ? 'border-current' : ''
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Icons */}
          <div className="flex items-center space-x-4 lg:space-x-6">
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="hover:opacity-70 transition-opacity"
            >
              <Search size={20} />
            </button>

            {user ? (
               <Link to="/profile" className="hidden md:block hover:opacity-70 transition-opacity" title="My Profile">
                   <User size={20} />
               </Link>
            ) : (
                <button
                  onClick={() => {
                    navigate('/login');
                    setIsMobileMenuOpen(false);
                  }}
                  className="hidden md:block hover:opacity-70 transition-opacity font-heading text-xs font-bold tracking-widest border border-current px-3 py-1 rounded-sm"
                >
                   LOGIN
                </button>
            )}

            <button onClick={openCart} className="hover:opacity-70 transition-opacity relative">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-brand-gold text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu Drawer */}
        <div
          className={`lg:hidden fixed inset-0 top-20 bg-white text-black transform transition-transform duration-300 ease-in-out z-40 ${
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex flex-col p-6 space-y-6 overflow-y-auto h-full pb-24">
            {NAVIGATION_ITEMS.map((item) => (
              <div key={item.label} className="border-b border-gray-100 pb-4">
                <Link
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="font-heading text-xl font-bold tracking-widest block"
                >
                  {item.label}
                </Link>
              </div>
            ))}
            <div className="pt-4 flex flex-col space-y-4">
              {user ? (
                  <Link to="/profile" className="flex items-center gap-2 font-heading text-sm font-bold" onClick={() => setIsMobileMenuOpen(false)}>
                    <User size={16} /> ACCOUNT
                  </Link>
              ) : (
                  <button
                    onClick={() => {
                      navigate('/login');
                      setIsMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-2 font-heading text-sm font-bold w-full text-left"
                  >
                    <User size={16} /> LOGIN
                  </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Search Overlay */}
      {isSearchOpen && (
        <div className="fixed inset-0 bg-white/95 z-[60] flex items-center justify-center p-4">
          <button
            onClick={() => setIsSearchOpen(false)}
            className="absolute top-8 right-8 p-2 hover:opacity-50"
          >
            <X size={32} />
          </button>
          <div className="w-full max-w-2xl">
            <input
              type="text"
              placeholder="SEARCH..."
              className="w-full text-3xl md:text-5xl font-heading font-bold border-b-2 border-black pb-4 bg-transparent outline-none uppercase placeholder:text-gray-300"
              autoFocus
            />
          </div>
        </div>
      )}
    </>
  );
};

export default AppHeader;
